import { useMemo, useState } from 'react';
import { Icon } from '@astral/ui';
import { isValidEmoji } from '@astral/shared';
import { useT, type TranslationKey } from '../providers/I18nProvider.js';
import { ALL_EMOJIS, EMOJI_CATEGORIES } from '../lib/emojiData.js';

const RECENT_KEY = 'astral.recentEmoji';
const RECENT_MAX = 24;

export function readRecentEmoji(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}

export function pushRecentEmoji(emoji: string): void {
  try {
    const next = [emoji, ...readRecentEmoji().filter((e) => e !== emoji)].slice(0, RECENT_MAX);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
}

export interface EmojiBoardProps {
  onSelect: (emoji: string) => void;
}

/**
 * Inline emoji board: a native-keyboard input (so users can enter ANY device
 * emoji, even ones our bundled set doesn't know), plus search, recent, and
 * categories. Rendered inline (not a Sheet) so it can be embedded without
 * stacking sheets — {@link EmojiPicker} wraps it in a Sheet for standalone use.
 */
export function EmojiBoard({ onSelect }: EmojiBoardProps): JSX.Element {
  const t = useT();
  const [query, setQuery] = useState('');
  const [cat, setCat] = useState<string>('smileys');
  const [native, setNative] = useState('');
  const recent = useMemo(() => readRecentEmoji(), []);

  const choose = (emoji: string): void => {
    pushRecentEmoji(emoji);
    onSelect(emoji);
  };

  const nativeValid = isValidEmoji(native.trim());
  const addNative = (): void => {
    const value = native.trim();
    if (!isValidEmoji(value)) return;
    setNative('');
    choose(value);
  };

  const q = query.trim().toLowerCase();
  const searchResults = q
    ? ALL_EMOJIS.filter(([emoji, kw]) => emoji.includes(q) || kw.includes(q)).map(([e]) => e)
    : null;

  const activeEmojis =
    cat === 'recent'
      ? recent
      : EMOJI_CATEGORIES.find((c) => c.key === cat)?.emojis.map(([e]) => e) ?? [];

  const tabs = [...(recent.length ? ['recent'] : []), ...EMOJI_CATEGORIES.map((c) => c.key)];

  return (
    <div className="emoji-picker">
      {/* Native device keyboard input — accepts any valid Unicode emoji. */}
      <div className="emoji-native">
        <input
          className="emoji-native__input"
          value={native}
          inputMode="text"
          autoComplete="off"
          autoCapitalize="none"
          placeholder={t('emoji.customPlaceholder')}
          aria-label={t('emoji.useKeyboard')}
          onChange={(event) => setNative(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              addNative();
            }
          }}
        />
        <button
          type="button"
          className="emoji-native__add"
          disabled={!nativeValid}
          onClick={addNative}
        >
          {native.trim() && !nativeValid ? t('emoji.invalid') : t('emoji.addEmoji')}
        </button>
      </div>

      <div className="searchbar">
        <Icon name="search" size={18} />
        <input
          value={query}
          placeholder={t('emoji.searchPlaceholder')}
          onChange={(event) => setQuery(event.target.value)}
        />
        {query && (
          <button
            className="muted"
            style={{ background: 'none', border: 'none', cursor: 'pointer' }}
            onClick={() => setQuery('')}
          >
            <Icon name="close" size={16} />
          </button>
        )}
      </div>

      {searchResults ? (
        <div className="emoji-grid">
          {searchResults.map((emoji) => (
            <button key={emoji} className="emoji-btn" onClick={() => choose(emoji)}>
              {emoji}
            </button>
          ))}
          {searchResults.length === 0 && (
            <p className="muted" style={{ gridColumn: '1 / -1' }}>
              {t('emoji.noResults')}
            </p>
          )}
        </div>
      ) : (
        <>
          <div className="emoji-tabs">
            {tabs.map((key) => (
              <button
                key={key}
                className={`emoji-tab${cat === key ? ' emoji-tab--active' : ''}`}
                onClick={() => setCat(key)}
              >
                {t(`emoji.categories.${key}` as TranslationKey)}
              </button>
            ))}
          </div>
          <div className="emoji-grid">
            {activeEmojis.map((emoji) => (
              <button key={emoji} className="emoji-btn" onClick={() => choose(emoji)}>
                {emoji}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
